import { useEffect, useState } from "react";

import { useDocument } from "../../../hooks/query";

export type ContractPricingDetails = {
    roofMaterials: number;
    roofSalesTax: number;
    roofLabor: number;
    gutterMaterials: number;
    gutterLabor: number;
    scopeSubtotal: number;
    profitMarginAmount: number;
    generalInsuranceAmount: number;
    jobOverheadAmount: number;
    salesTaxRate: number;
    targetMarginPercent: number;
    overheadPercent: number;
    jobOverheadPercent: number;
    bidAmountOverride: number | null;
};

export type ContractPricingBreakdownLine = {
    label: string;
    amount: number;
    amountFormatted: string;
    isTotal?: boolean;
};

export type ContractPricingResult = {
    RoofPrice: number;
    GutterPrice: number;
    TotalPrice: number;
    RoofPriceFormatted: string;
    GutterPriceFormatted: string;
    TotalPriceFormatted: string;
    PaymentSchedule: string;
    UpgradeNotes?: string;
    UpgradeAmount?: string;
    Breakdown: ContractPricingBreakdownLine[];
    Details: ContractPricingDetails;
};

export function formatFinanceCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(amount || 0);
}

export default function useFinanceBreakdown(
    projectId?: string,
    estimateOptionId?: string | null,
) {
    const { useGetContractPricing } = useDocument();
    const getContractPricingSvc = useGetContractPricing();

    const [pricing, setPricing] = useState<ContractPricingResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadPricing = async (id: string, optionId?: string | null) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getContractPricingSvc.mutateAsync({
                ProjectId: id,
                ...(optionId ? { EstimateOptionId: optionId } : {}),
            });
            setPricing(response as ContractPricingResult);
        } catch (err: any) {
            setPricing(null);
            setError(
                err?.message ||
                    "Failed to load contract pricing. Select suppliers on materials/labor first.",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!projectId) {
            setPricing(null);
            setError(null);
            return;
        }
        loadPricing(projectId, estimateOptionId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, estimateOptionId]);

    return {
        pricing,
        loading,
        error,
        reload: () => {
            if (projectId) return loadPricing(projectId, estimateOptionId);
        },
    };
}
